import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Package, Box, Layers, Sparkles, Building2, Home, ShirtIcon, Truck, CheckCircle2, Ship, FileCheck, Globe, Container } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Services = () => {
  const packagingServices = [
    {
      icon: Box,
      title: 'Cardboard Boxes',
      description: 'High-quality cardboard boxes in various sizes for shipping, storage, and retail packaging.',
    },
    {
      icon: Layers,
      title: 'Custom Packaging',
      description: 'Tailored packaging solutions designed to meet your specific product and brand requirements.',
    },
    {
      icon: Package,
      title: 'Bulk Packaging',
      description: 'Large-scale packaging materials for industrial and commercial applications.',
    },
  ];

  const importExportServices = [
    {
      icon: Ship,
      title: 'Import & Export',
      description: 'Comprehensive import and export services for packaging materials and products across international markets.',
    },
    {
      icon: FileCheck,
      title: 'Shipping Clearance',
      description: 'Expert customs clearance and documentation services in India and overseas destinations.',
    },
    {
      icon: Globe,
      title: 'Global Logistics',
      description: 'End-to-end logistics solutions connecting India with international markets seamlessly.',
    },
  ];

  const cleaningServices = [
    {
      icon: Home,
      title: 'Residential Cleaning',
      description: 'Comprehensive home cleaning services including regular and deep cleaning options.',
    },
    {
      icon: Building2,
      title: 'Commercial Cleaning',
      description: 'Professional cleaning for offices, retail spaces, and commercial establishments.',
    },
    {
      icon: Sparkles,
      title: 'Deep Cleaning',
      description: 'Intensive cleaning and sanitization services for complete hygiene solutions.',
    },
  ];

  const laundryServices = [
    {
      icon: ShirtIcon,
      title: 'Laundry & Dry Cleaning',
      description: 'Expert washing and dry cleaning services for all types of garments and fabrics.',
    },
    {
      icon: Truck,
      title: 'Pickup & Delivery',
      description: 'Convenient door-to-door service for hassle-free laundry experience.',
    },
    {
      icon: Building2,
      title: 'Commercial Laundry',
      description: 'Large-scale laundry solutions for hotels, hospitals, and businesses.',
    },
  ];

  const testimonials = [
    {
      quote: "Excellent packaging quality and bulk production capabilities. AUMKAR International has been our trusted partner for years.",
      author: "Rajesh Kumar",
      company: "Tech Solutions Pvt Ltd",
      rating: 5,
    },
    {
      quote: "Professional cleaning services that exceed expectations. Remas team is always punctual and thorough.",
      author: "Ahmed Al-Farsi",
      company: "Al-Farsi Trading LLC",
      rating: 5,
    },
    {
      quote: "Goodluck Laundry's pickup service is a game-changer. My clothes always come back perfectly pressed.",
      author: "Sarah Mohammed",
      company: "Individual Customer",
      rating: 5,
    },
  ];

  return (
    <div data-testid="services-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-blue-700 mb-4">
            Our Services
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 mb-6">
            Comprehensive Solutions for Your Needs
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg">
            From packaging manufacturing to professional cleaning and laundry services, we deliver excellence across all our offerings.
          </p>
        </div>
      </section>

      {/* Packaging Services */}
      <section id="packaging" data-testid="packaging-services" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-blue-700 rounded-xl flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
                Packaging Manufacturing
              </h2>
              <p className="text-slate-600">Aumkar International - Dundigal, India</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packagingServices.map((service, index) => (
              <Card key={index} className="border-slate-200 card-hover service-card">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-6 service-icon transition-all">
                    <service.icon className="w-7 h-7 text-blue-700" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                  <p className="text-slate-600">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/contact?service=packaging">
              <Button
                data-testid="btn-packaging-quote"
                className="bg-blue-700 hover:bg-blue-800 text-white rounded-full group"
              >
                Get Packaging Quote
                <ArrowRight className="ml-2 w-4 h-4 btn-arrow" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Import & Export Services */}
      <section id="import-export" data-testid="import-export-services" className="section-padding bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
              <Ship className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                Import & Export Services
              </h2>
              <p className="text-slate-400">Aumkar Packaging - India & International</p>
            </div>
          </div>
          
          {/* Image Gallery */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            <div className="md:col-span-2 relative rounded-2xl overflow-hidden h-64 md:h-80">
              <img
                src="https://images.pexels.com/photos/1427107/pexels-photo-1427107.jpeg"
                alt="Shipping containers at port"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <p className="text-white font-bold text-xl">Global Shipping Network</p>
                <p className="text-slate-300 text-sm">Connecting India to the world</p>
              </div>
            </div>
            <div className="grid grid-rows-2 gap-4">
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/4483610/pexels-photo-4483610.jpeg"
                  alt="Warehouse operations"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                <p className="absolute bottom-3 left-3 text-white text-sm font-medium">Warehouse Operations</p>
              </div>
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/6169668/pexels-photo-6169668.jpeg"
                  alt="Customs clearance documentation"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                <p className="absolute bottom-3 left-3 text-white text-sm font-medium">Customs Clearance</p>
              </div>
            </div>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {importExportServices.map((service, index) => (
              <Card key={index} className="bg-slate-800 border-slate-700 card-hover">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-blue-600/20 rounded-xl flex items-center justify-center mb-6">
                    <service.icon className="w-7 h-7 text-blue-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                  <p className="text-slate-400">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Key Features */}
          <div className="bg-slate-800 rounded-2xl p-8 mb-8">
            <h3 className="text-xl font-bold text-white mb-6">Our Import & Export Capabilities</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                'Customs Documentation',
                'Shipping Clearance in India',
                'International Freight',
                'Overseas Clearance',
                'Import Licensing',
                'Export Compliance',
                'Door-to-Door Delivery',
                'Cargo Insurance',
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  <span className="text-slate-300 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <Link to="/contact?service=import-export">
              <Button
                data-testid="btn-import-export-quote"
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full group"
              >
                Get Import/Export Quote
                <ArrowRight className="ml-2 w-4 h-4 btn-arrow" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Cleaning Services */}
      <section id="cleaning" data-testid="cleaning-services" className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-blue-700 rounded-xl flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
                Cleaning Services
              </h2>
              <p className="text-slate-600">Remas Cleaning Services - Oman</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {cleaningServices.map((service, index) => (
              <Card key={index} className="border-slate-200 bg-white card-hover service-card">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-6 service-icon transition-all">
                    <service.icon className="w-7 h-7 text-blue-700" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                  <p className="text-slate-600">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/contact?service=cleaning">
              <Button
                data-testid="btn-cleaning-quote"
                className="bg-blue-700 hover:bg-blue-800 text-white rounded-full group"
              >
                Get Cleaning Quote
                <ArrowRight className="ml-2 w-4 h-4 btn-arrow" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Laundry Services */}
      <section id="laundry" data-testid="laundry-services" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-blue-700 rounded-xl flex items-center justify-center">
              <ShirtIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
                Laundry Services
              </h2>
              <p className="text-slate-600">Goodluck Laundry Services - Oman</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {laundryServices.map((service, index) => (
              <Card key={index} className="border-slate-200 card-hover service-card">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-6 service-icon transition-all">
                    <service.icon className="w-7 h-7 text-blue-700" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                  <p className="text-slate-600">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/contact?service=laundry">
              <Button
                data-testid="btn-laundry-quote"
                className="bg-blue-700 hover:bg-blue-800 text-white rounded-full group"
              >
                Get Laundry Quote
                <ArrowRight className="ml-2 w-4 h-4 btn-arrow" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="section-padding bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] font-semibold text-blue-400 mb-4">
              Testimonials
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              What Our Clients Say
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-slate-800 border-slate-700">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="text-yellow-400">★</span>
                    ))}
                  </div>
                  <p className="text-slate-300 mb-6 leading-relaxed">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-semibold text-white">{testimonial.author}</p>
                    <p className="text-sm text-slate-400">{testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section data-testid="why-choose-section" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] font-semibold text-blue-700 mb-4">
                Why Choose AUMKAR
              </p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6">
                Your Trusted Partner for Quality
              </h2>
              <ul className="space-y-4">
                {[
                  'Industry-leading packaging manufacturing',
                  'International presence in India & Oman',
                  'High-quality standards & certifications',
                  'Reliable and timely delivery',
                  'Skilled workforce and management',
                  'Competitive and transparent pricing',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.pexels.com/photos/4481259/pexels-photo-4481259.jpeg"
                alt="Quality packaging"
                className="rounded-xl w-full h-48 object-cover"
              />
              <img
                src="https://images.pexels.com/photos/4239036/pexels-photo-4239036.jpeg"
                alt="Cleaning services"
                className="rounded-xl w-full h-48 object-cover mt-8"
              />
              <img
                src="https://images.pexels.com/photos/5591581/pexels-photo-5591581.jpeg"
                alt="Laundry services"
                className="rounded-xl w-full h-48 object-cover"
              />
              <img
                src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg"
                alt="Team collaboration"
                className="rounded-xl w-full h-48 object-cover mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-blue-700">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
            Contact us today for a free quote on any of our services. Our team is ready to assist you.
          </p>
          <Link to="/contact?quote=true">
            <Button
              data-testid="services-cta-btn"
              className="bg-white text-blue-700 hover:bg-slate-100 rounded-full px-8 py-6 text-base group"
            >
              Get a Free Quote
              <ArrowRight className="ml-2 w-4 h-4 btn-arrow" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
